import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Plus, SquarePen, Trash2, Calendar, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllBlogs, deleteBlog } from '../../Services/BlogsManagement/BlogsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import GlobalPagination from '../../Components/Common/Globalpagination';

const GetBlogs = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [blogsData, setBlogsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Pagination & Search state
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [search, setSearch] = useState('');
    const [selectedType, setSelectedType] = useState('');

    const fetchBlogsData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllBlogs(search, page, rowsPerPage, selectedType);

            if (response && response.success && response.data) {
                setBlogsData(response.data);
                setTotalRecords(response.total || 0);
            } else {
                setBlogsData([]);
                setTotalRecords(0);
            }
        } catch (err) {
            console.error(err);
            showSnackbar("Failed to fetch blogs. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, selectedType, showSnackbar]);

    useEffect(() => {
        fetchBlogsData();
    }, [fetchBlogsData]);

    const handleAddClick = () => {
        navigate('/blogs-management/add');
    };

    const handleEditClick = (blog) => {
        navigate('/blogs-management/edit', { state: { blog } });
    };

    const handleOpenDelete = (id) => {
        setItemToDelete(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            setIsDeleting(true);
            setDeleteError(null);
            const response = await deleteBlog(itemToDelete);
            if (response && response.success) {
                fetchBlogsData();
                setDeleteModalOpen(false);
                setItemToDelete(null);
                showSnackbar("Blog deleted successfully!", "success");
            } else {
                setDeleteError(response?.message || "Failed to delete blog.");
            }
        } catch (err) {
            console.error("Error deleting blog:", err);
            const errorMessage = err.response?.data?.message || err.message || "Failed to delete blog.";
            setDeleteError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh' }}>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography
                        sx={{
                            color: '#1E293B',
                            fontSize: { xs: '1.25rem', md: '1.4rem' },
                            fontWeight: 500
                        }}
                    >
                        Blogs Management
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, maxWidth: { xs: '100%', md: '520px' }, width: { xs: '100%', md: 'auto' } }}>
                        <OutlinedInput
                            fullWidth
                            size="small"
                            placeholder="Search blogs by title..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search size={16} color="#64748B" />
                                </InputAdornment>
                            }
                            sx={{
                                borderRadius: '6px',
                                bgcolor: '#FFFFFF',
                                height: '38px',
                                fontSize: '0.8rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 125 }}>
                            <InputLabel sx={{ fontSize: '0.8rem', color: '#64748B', '&.Mui-focused': { color: '#6E8964' } }}>Type</InputLabel>
                            <Select
                                value={selectedType}
                                label="Type"
                                onChange={(e) => {
                                    setSelectedType(e.target.value);
                                    setPage(1);
                                }}
                                sx={{
                                    borderRadius: '6px',
                                    height: '38px',
                                    bgcolor: '#FFFFFF',
                                    fontSize: '0.8rem',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                    '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' },
                                }}
                            >
                                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All Types</MenuItem>
                                <MenuItem value="blog" sx={{ fontSize: '0.8rem' }}>Blog</MenuItem>
                                <MenuItem value="guide" sx={{ fontSize: '0.8rem' }}>Guide</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                        <PermissionWrapper permission="view" mode="disable">
                            <Button disableElevation disableFocusRipple disableRipple
                                variant="outlined"
                                onClick={() => navigate('/blogs-management/categories')}
                                sx={{
                                    borderRadius: '6px',
                                    px: 2.5,
                                    py: 0.85,
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    color: '#475569',
                                    borderColor: '#E2E8F0',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Blog Categories
                            </Button>
                        </PermissionWrapper>
                        <PermissionWrapper permission="create" mode="disable">
                            <Button disableElevation disableFocusRipple disableRipple
                                variant="contained"
                                startIcon={<Plus size={16} />}
                                onClick={handleAddClick}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    color: '#FFFFFF',
                                    borderRadius: '6px',
                                    px: 2.5,
                                    py: 0.85,
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    boxShadow: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Add Blog
                            </Button>
                        </PermissionWrapper>
                    </Stack>
                </Box>

                {/* Table */}
                <Paper sx={{ borderRadius: "6px 6px 0 0", border: "1px solid #E2E8F0", overflow: "hidden", bgcolor: '#FFFFFF' }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1250 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>S.No</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Cover Image</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Title</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Category</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Type</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Status</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Created By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Updated By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Created At</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={24} sx={{ color: '#6E8964' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : blogsData.length > 0 ? (
                                    blogsData.map((blog, idx) => (
                                        <TableRow key={blog.id} sx={{
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {(page * rowsPerPage) + idx + 1}
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Avatar
                                                    src={blog.coverImage}
                                                    variant="rounded"
                                                    sx={{ width: 44, height: 30, borderRadius: '4px', border: '1px solid #E2E8F0' }}
                                                />
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 240 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        color: '#1E293B',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={blog.title}
                                                >
                                                    {blog.title || '—'}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={blog.category?.name || "General"}
                                                    size="small"
                                                    sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 500, fontSize: '0.7rem', borderRadius: '4px', height: 22 }}
                                                />
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.25,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        bgcolor: blog.type === 'guide' ? '#EFF6FF' : '#F5F3FF',
                                                        color: blog.type === 'guide' ? '#1E40AF' : '#6B21A8',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize'
                                                    }}
                                                >
                                                    {blog.type || "—"}
                                                </Box>
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.25,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        bgcolor: blog.isActive ? '#E8F5E8' : '#FFEBEE',
                                                        color: blog.isActive ? '#2E7D32' : '#C62828',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {blog.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {blog.cuName || blog.creator?.name || '-'}
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {blog.uuName || '-'}
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                                    <Calendar size={13} color="#94A3B8" />
                                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                                                        {formatDate(blog.createdAt)}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', color: '#64748B', fontSize: '0.75rem' }}>
                                                {formatDate(blog.updatedAt)}
                                            </TableCell>

                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#64748B', p: 0.5 }}
                                                                onClick={() => handleEditClick(blog)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#EF4444', p: 0.5 }}
                                                                onClick={() => handleOpenDelete(blog.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            No blogs found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                        </Table>
                    </TableContainer>
                </Paper>

                <GlobalPagination
                    page={page}
                    pageSize={rowsPerPage}
                    totalRecords={totalRecords}
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newSize) => {
                        setRowsPerPage(newSize);
                        setPage(1);
                    }}
                    loading={loading}
                />

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Blog"
                    description="Are you sure you want to delete this blog? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetBlogs;
